import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AUTH_INTENT_GUIDE, parseAuthIntent } from '@/features/auth/constants';
import { handlePostAuth } from '@/features/auth/utils/handlePostAuth';
import { STORAGE_KEYS } from '@/utils/constants';
import { brand } from '@/shared/config/brand';
import type { AuthResponse } from '@/types';
import { PhoneAuthForm } from '../components/PhoneAuthForm';

function persistAuthIntent(intent: ReturnType<typeof parseAuthIntent>) {
  if (intent === AUTH_INTENT_GUIDE) {
    sessionStorage.setItem(STORAGE_KEYS.AUTH_INTENT, intent);
  } else {
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_INTENT);
  }
}

export function PhoneAuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth, setMe } = useAuth();
  const from = (location.state as { from?: string })?.from;
  const intent = parseAuthIntent(location.search);

  useEffect(() => {
    persistAuthIntent(intent);
  }, [intent]);

  const handleOtpSent = (phone: string) => {
    localStorage.setItem(STORAGE_KEYS.OTP_PHONE, phone);
    persistAuthIntent(intent);
    navigate('/auth/verify-otp', { state: { phone, from, intent } });
  };

  const handleGoogleSuccess = async (data: AuthResponse) => {
    setAuth(data);
    if (data.requiresProfileSetup) {
      // TravelerProfileGate (AuthLayout) will prompt; stay put until complete.
      return;
    }
    const route = await handlePostAuth(data, { intent, from, setMe });
    sessionStorage.removeItem(STORAGE_KEYS.AUTH_INTENT);
    navigate(route, { replace: true });
  };

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 380 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 3 },
            borderRadius: 'var(--radius-xl)',
            border: '1px solid',
            borderColor: 'rgba(26,46,26,0.08)',
            bgcolor: 'rgba(255,255,255,0.72)',
            backdropFilter: 'blur(10px)',
            mb: 2.5,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              textAlign: 'center',
              mb: 1,
              color: 'secondary.main',
              fontWeight: 700,
              letterSpacing: '0.14em',
            }}
          >
            Welcome
          </Typography>

          <Typography
            variant="h3"
            sx={{
              mb: 1,
              fontSize: { xs: '1.625rem', sm: '1.95rem' },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              textAlign: 'center',
            }}
          >
            Sign in to {brand.uiName}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, lineHeight: 1.7, textAlign: 'center', maxWidth: 320, mx: 'auto' }}
          >
            Continue with your mobile number or Google to explore destinations and connect with
            trusted local guides.
          </Typography>

          <PhoneAuthForm
            onOtpSent={handleOtpSent}
            onGoogleSuccess={handleGoogleSuccess}
            intent={intent}
            from={from}
            smsConsentSource="web_phone_auth"
          />
        </Paper>

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          sx={{ lineHeight: 1.7, px: 1 }}
        >
          By continuing, you agree to our{' '}
          <Box
            component={RouterLink}
            to="/terms"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            Terms &amp; Conditions
          </Box>{' '}
          and{' '}
          <Box
            component={RouterLink}
            to="/privacy-policy"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
          >
            Privacy Policy
          </Box>
          .
        </Typography>
      </Box>
    </Box>
  );
}
